import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTypedDispatch, useTypedSelector } from '@profile/src/redux/utils';
import PageContent from '@profile/src/Components/PageContent';
import { makeSelectCosmeticDetail } from './selectors';
import styles from './styles.module.scss';
import EmptyList from '@profile/src/Components/EmptyList';
import Spinner from '@profile/src/Components/Spinner';
import PaginationControl from '@profile/src/Components/PaginationControl_V2';
import { ITEM_PER_PAGE } from './constants';
import { fetchCosmeticConfigs, fetchCosmeticInventory, fetchSelectedCosmetic } from '@profile/src/redux/actions';
import ItemCardPlaceholder from './ItemCardPlaceholder';
import ItemCard from './ItemCard';
import useParamsRoute from '@profile/src/hooks/useRouteParams';
import { MenuItem } from '@profile/src/utils/navigate';
import { CategoryKey } from '@profile/src/Profile_V2/Components/Inventory/types';
import { normaliseSearchUrl } from '@profile/src/utils/query';
// eslint-disable-next-line @typescript-eslint/ban-types
const SearchResult = /*#__PURE__*/React.forwardRef((_, ref) => {
  const dispatch = useTypedDispatch();
  const history = useHistory();
  const {
    page = 1,
    types,
    sort
  } = useParamsRoute();
  const items = useTypedSelector(makeSelectCosmeticDetail());
  const totalItems = useTypedSelector(state => state.cosmeticInventory.totalItems);
  const loading = useTypedSelector(state => state.cosmeticInventory.loading);
  const invalidated = useTypedSelector(state => state.cosmeticInventory.invalidated);
  const error = useTypedSelector(state => state.cosmeticInventory.error);
  const totalPages = Math.ceil(totalItems / ITEM_PER_PAGE);

  const onFetch = (page, types, sort) => {
    const searchQuery = normaliseSearchUrl({
      tab: MenuItem.INVENTORY,
      category: CategoryKey.Cosmetic,
      page,
      types,
      sort
    });
    history.push({
      search: searchQuery
    });
  };

  React.useImperativeHandle(ref, () => ({
    onFetch: ({
      page,
      types,
      sort
    }) => {
      onFetch(page, types, sort);
    }
  }));

  const onPageChange = page => {
    onFetch(page, types, sort);
  };

  React.useEffect(() => {
    dispatch(fetchCosmeticConfigs());
    dispatch(fetchSelectedCosmetic());
    dispatch(fetchCosmeticInventory({
      from: (page - 1) * ITEM_PER_PAGE,
      size: ITEM_PER_PAGE,
      types: types !== 'all' && types || undefined,
      sort
    }));
  }, [dispatch, page, types, sort]);

  const renderContent = () => {
    if (items && !items.length) {
      return /*#__PURE__*/React.createElement(EmptyList, {
        title: "Sorry, no result found.",
        content: "Please try another search",
        className: styles.empty
      });
    }

    return /*#__PURE__*/React.createElement("div", {
      className: styles.content
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.grid
    }, items && items.map((item, index) => {
      return item ? /*#__PURE__*/React.createElement(ItemCard, {
        key: item.id,
        className: styles.item,
        cosmetic: item,
        onPress: () => history.push({
          pathname: `/cosmetic/${item.id}`,
          state: {
            isFromInventory: true
          }
        })
      }) : /*#__PURE__*/React.createElement(ItemCardPlaceholder, {
        key: `placeholder_${index}`,
        className: styles.item
      });
    })), /*#__PURE__*/React.createElement(PaginationControl, {
      className: styles.paging,
      page: page,
      onPageChange: onPageChange,
      totalPages: totalPages
    }));
  };

  const renderLoading = () => {
    return /*#__PURE__*/React.createElement(Spinner, {
      className: styles.spinner
    });
  };

  const renderError = () => /*#__PURE__*/React.createElement(EmptyList, {
    title: "Sorry, something went wrong.",
    content: "Please try again.",
    className: styles.empty
  });

  return /*#__PURE__*/React.createElement(PageContent, {
    invalidated: invalidated,
    loading: loading,
    error: !!error,
    renderContent: renderContent,
    renderLoading: renderLoading,
    renderError: renderError
  });
});
SearchResult.displayName = 'SearchResult';
export default SearchResult;